import React from "react";
import { useSelector } from "react-redux";
import { CircularProgress, Grid2 } from "@mui/material";
import PageTitle from "../../../components/Common/PageTitle/index";
import ContentCard from "../../../components/Content/ContentCard";
import PolicySection from "./components/PolicySection";
import IPHCPractitionerModal from "../../../components/Modals/IPHCPractitionerModal";
import PractitionerDisclaimer from "../../../components/Modals/PractitionerDisclaimer";
import {
  ConfirmationSnackbar,
  ErrorSnackbar,
} from "../../../components/Common/CustomSnackbar";
import { categorizedPolicies } from "../../../assets/data/data";
import NirvaLogo from "../../../assets/images/nirva_logo.png";

import { FooterText } from "../../../components/Common/FooterText";
import {
  StyledBackdrop,
  RootWrapper,
  SectionTitle,
  SectionTitleLarge,
} from "./Nirva.styles";
import {
  PAGE_TITLE,
  PAGE_TEXT,
  SIGN_UP_CARD,
  SECTION_TITLES,
  SNACKBAR_MESSAGES,
  FOOTER_TEXT,
} from "./Nirva.constants";
import { useNirvaPractitioner } from "./Nirva.hooks";
import PaymentSetup from "../PaymentSetup/PaymentSetup";

const NirvaPage = () => {
  const {
    loading,
    success,
    errorState,
    autoSaveStatus,
    submitData,
    setSubmitData,
    iphcPractitioner,
    disclaimerModalState,
    setDisclaimerModalState,
    modalState,
    setModalState,
    setErrorState,
    setSuccess,
    handleSubmit,
    handleFileChange,
    handleFileDelete,
    handleImageUpload,
    handleDisclaimerSave,
  } = useNirvaPractitioner();

  const { userData } = useSelector((state) => state.user);
  const isPractitioner =
    userData?.role !== "patient" &&
    iphcPractitioner &&
    Object.keys(iphcPractitioner).length > 0;

  // Payment Setup is hidden for everyone EXCEPT Ann Marie — payments are moving
  // to SimplePractice, but Ann Marie keeps the dashboard payment page. Matched by
  // email allow-list (kept in code, not deleted).
  const PAYMENT_SETUP_ALLOWED_EMAILS = ["amm@ammhypnotherapy.com"];
  const showPaymentSetup = PAYMENT_SETUP_ALLOWED_EMAILS.includes(
    (userData?.email || "").toLowerCase()
  );

  const data = [SIGN_UP_CARD];

  const successMessage = iphcPractitioner
    ? SNACKBAR_MESSAGES.EDIT_SUBMITTED
    : SNACKBAR_MESSAGES.SUBMISSION_RECEIVED;

  return (
    <RootWrapper>
      <StyledBackdrop open={loading}>
        <CircularProgress color="inherit" />
      </StyledBackdrop>
      <ErrorSnackbar
        error={errorState.error}
        handleCloseError={() => setErrorState({ error: false, message: "" })}
        errorMsg={errorState.message}
      />
      <ConfirmationSnackbar
        snackbarState={success}
        handleSnackbarClose={() => setSuccess(false)}
        text={successMessage}
      />
      <PageTitle title={PAGE_TITLE} text={PAGE_TEXT} icon={NirvaLogo} />
      <Grid2 container spacing={2} sx={{ mt: 4 }}>
        {data.map((item, index) => (
          <React.Fragment key={item.id}>
            <ContentCard
              item={item}
              onClick={() => setDisclaimerModalState(true)}
              clickable={() => true}
              height={item.height || "200px"}
              size={item.size}
            />
            {index === 2 && (
              <Grid2 item xs={12}>
                <SectionTitle>{SECTION_TITLES.LEARN_MORE}</SectionTitle>
              </Grid2>
            )}
          </React.Fragment>
        ))}
      </Grid2>

      {isPractitioner && showPaymentSetup && (
        <>
          <SectionTitleLarge>Payment Setup</SectionTitleLarge>
          <PaymentSetup />
        </>
      )}

      <SectionTitleLarge>
        {SECTION_TITLES.PRACTITIONER_RESOURCES}
      </SectionTitleLarge>

      <PolicySection
        title={SECTION_TITLES.ONBOARDING}
        policies={categorizedPolicies.onboarding}
      />

      <PolicySection
        title={SECTION_TITLES.SESSION_RESOURCES}
        policies={categorizedPolicies.sessionResources}
      />

      <PolicySection
        title={SECTION_TITLES.SOPS_POLICIES}
        policies={categorizedPolicies.sopsPolicies}
      />

      <FooterText>{FOOTER_TEXT}</FooterText>

      <PractitionerDisclaimer
        open={disclaimerModalState}
        handleClose={() => setDisclaimerModalState(false)}
        onSave={handleDisclaimerSave}
      />
      <IPHCPractitionerModal
        open={modalState}
        handleClose={() => setModalState(false)}
        submitData={submitData}
        setSubmitData={setSubmitData}
        handleSubmit={handleSubmit}
        handleImageUpload={handleImageUpload}
        handleFileChange={handleFileChange}
        handleFileDelete={handleFileDelete}
        setErrorState={setErrorState}
        autoSaveStatus={autoSaveStatus}
        loading={loading}
      />
    </RootWrapper>
  );
};

export default NirvaPage;
